'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';

type Status = 'idle' | 'loading' | 'success' | 'error';

type FaqItem = {
  id: string;
  q: string;
  a: string;
  tags?: string[];
};

const MOCK_FAQS: FaqItem[] = [
  {
    id: 'sbp-1',
    q: 'How do I apply for a Single Business Permit (SBP) in Bomet?',
    a: '',
    tags: ['business permit', 'sbp', 'licensing', 'bomet pay'],
  },
  {
    id: 'parking-1',
    q: 'How do I pay daily or seasonal parking in Bomet?',
    a: '',
    tags: ['parking', 'mpesa', 'payments', 'cbd'],
  },
  {
    id: 'rates-1',
    q: 'Where can I check and pay land rates?',
    a: '',
    tags: ['land rates', 'property', 'payments'],
  },
  {
    id: 'fire-1',
    q: 'How do I get a Fire Safety Certificate?',
    a: '',
    tags: ['fire certificate', 'inspection', 'public safety'],
  },
  {
    id: 'construction-1',
    q: 'What are the steps for a building plan approval?',
    a: '',
    tags: ['construction permits', 'building plans', 'development control'],
  },
  {
    id: 'waste-1',
    q: 'Can I apply for commercial garbage collection?',
    a: '',
    tags: ['waste', 'garbage', 'environment'],
  },
  {
    id: 'health-1',
    q: 'Do I need a public health licence for a restaurant?',
    a: '',
    tags: ['public health', 'restaurant', 'license'],
  },
];

const POPULAR = [
  'parking',
  'business permit',
  'land rates',
  'fire certificate',
  'construction permits',
];

const FAQSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [results, setResults] = useState<FaqItem[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const isLoading = status === 'loading';

  const handleSearch = async (value: string) => {
    setStatus('loading');
    setMessage(null);
    setShowResults(true);

    await new Promise((r) => setTimeout(r, 700));

    const v = value.trim().toLowerCase();
    const filtered = MOCK_FAQS.filter((f) => {
      const hay = `${f.q} ${f.a} ${f.tags?.join(' ') ?? ''}`.toLowerCase();
      return v ? hay.includes(v) : true;
    });

    setResults(filtered);
    setStatus('success');
    setMessage(
      filtered.length
        ? `Found ${filtered.length} result${filtered.length > 1 ? 's' : ''}.`
        : 'No results found.'
    );
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (isLoading) return;
    handleSearch(query);
  };

  const onPopularClick = (term: string) => {
    if (isLoading) return;
    setQuery(term);
    handleSearch(term);
  };

  const dismissResults = () => {
    setShowResults(false);
    setStatus('idle');
    setMessage(null);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissResults();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const bannerStyle =
    status === 'success'
      ? 'bg-green-50 text-green-800 border border-green-200'
      : status === 'error'
      ? 'bg-red-50 text-red-800 border border-red-200'
      : 'bg-transparent';

  const resultsToShow = useMemo(() => results.slice(0, 8), [results]);

  return (
    <div className="w-full mx-auto flex flex-col items-center px-4 sm:px-6">
      <h2 className="mt-2 text-2xl sm:text-3xl md:text-[34px] leading-tight font-extrabold text-[var(--color-primary)] tracking-tight text-center">
        - FAQ -
      </h2>

      <form onSubmit={onSubmit} className="mt-2 w-full flex justify-center">
        <div className="w-[500px] max-w-md sm:max-w-2xl md:max-w-3xl h-12 bg-white border border-[#0b0b0b] rounded-full flex items-center focus-within:ring-2 focus-within:ring-[var(--color-primary)]">
          <input
            type="search"
            placeholder="Ask a question (e.g., parking, SBP, land rates)"
            aria-label="Ask a question"
            className="flex-1 h-full bg-transparent px-4 sm:px-5 text-sm sm:text-base leading-[22px] text-[#2b2b2b] placeholder-[#6b6b6b] focus:outline-none"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isLoading}
          />

          <button
            type="submit"
            disabled={isLoading}
            aria-label="Search"
            className="shrink-0 mr-2 sm:mr-3 rounded-full p-2 text-white disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {isLoading ? (
              <svg
                className="animate-spin h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  opacity="0.25"
                />
                <path
                  d="M22 12a10 10 0 00-10-10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
              </svg>
            ) : (
              <FaSearch aria-hidden="true" />
            )}
          </button>
        </div>
      </form>

      <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-[13px] leading-[18px] text-[#0b0b0b]">
        <span className="font-bold">Popular help topics:</span>
        {POPULAR.map((t) => (
          <button
            key={t}
            onClick={() => onPopularClick(t)}
            disabled={isLoading}
            className="rounded-full border border-[#d6d6d6] px-3 py-1 text-xs sm:text-sm hover:bg-gray-50 disabled:opacity-60"
            aria-label={`Search topic ${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-2 w-full flex justify-center" aria-live="polite">
        <div className="w-full max-w-md sm:max-w-2xl md:max-w-3xl">
          <div
            className={`transition-all duration-300 rounded-md px-3 py-2 text-sm ${
              message ? bannerStyle : 'h-0 p-0 overflow-hidden'
            }`}
          >
            {message}
          </div>
        </div>
      </div>

      {showResults && (
        <div className="mt-2 w-full max-w-md sm:max-w-2xl md:max-w-3xl">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-[#333]">Search results</p>
            <button
              type="button"
              onClick={dismissResults}
              className="inline-flex items-center gap-2 text-xs sm:text-sm px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50"
              aria-label="Dismiss results"
              title="Dismiss results (Esc)"
            >
              <FaTimes className="opacity-70" />
              <span className="hidden sm:inline">Dismiss</span>
            </button>
          </div>

          {status === 'loading' && (
            <div className="space-y-3">
              <div className="h-6 w-2/3 bg-gray-100 animate-pulse rounded" />
              <div className="h-4 w-full bg-gray-100 animate-pulse rounded" />
              <div className="h-4 w-5/6 bg-gray-100 animate-pulse rounded" />
            </div>
          )}

          {status === 'success' && resultsToShow.length > 0 && (
            <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 overflow-hidden bg-white">
              {resultsToShow.map((item) => (
                <li key={item.id} className="p-4">
                  <p className="font-semibold text-[#111]">{item.q}</p>
                  <p className="mt-1 text-sm text-[#333]">{item.a}</p>
                  {item.tags?.length ? (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {item.tags.map((tg) => (
                        <span
                          key={tg}
                          className="text-xs border border-gray-200 rounded-full px-2 py-0.5"
                        >
                          {tg}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {status === 'success' && resultsToShow.length === 0 && (
            <div className="text-sm text-[#333]">Try a different keyword.</div>
          )}
        </div>
      )}

      <div className="w-full h-[1px] sm:h-[1px] bg-[#f9f9f9] mt-2" />
    </div>
  );
};

export default FAQSearch;
